import { useLayoutEffect, type RefObject } from 'react';

/** Keep a floating CTA outside the usable scroll viewport as its content resizes.
 * Layout pixels (rather than transformed DOMRects) also work in scaled previews. */
export function useStickyScrollClearance(
  containerRef: RefObject<HTMLDivElement>,
  contentRef: RefObject<HTMLDivElement>,
  stickyRef: RefObject<HTMLDivElement>,
  enabled: boolean,
  anchor: 'top' | 'bottom',
  anchorOffset = 0,
) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    const sticky = stickyRef.current;
    if (!enabled || !container || !content || !sticky) return;
    let top = 0;
    let bottom = 0;
    const update = () => {
      // Reconstruct the unreserved viewport so observer callbacks cannot compound margins.
      const naturalTop = content.offsetTop - top;
      const naturalBottom = Math.min(container.clientHeight, content.offsetTop + content.offsetHeight + bottom);
      const available = Math.max(0, naturalBottom - naturalTop);
      const visible = sticky.offsetHeight > 0;
      const nextTop = visible && anchor === 'top'
        ? Math.min(available, Math.max(0, sticky.offsetTop + sticky.offsetHeight - naturalTop)) : 0;
      const nextBottom = visible && anchor === 'bottom'
        ? Math.min(available, Math.max(0, naturalBottom - sticky.offsetTop)) : 0;
      if (nextTop !== top) content.style.marginTop = `${nextTop}px`;
      if (nextBottom !== bottom) content.style.marginBottom = `${nextBottom}px`;
      top = nextTop;
      bottom = nextBottom;
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    observer?.observe(container);
    observer?.observe(content);
    observer?.observe(sticky);
    return () => {
      observer?.disconnect();
      content.style.removeProperty('margin-top');
      content.style.removeProperty('margin-bottom');
    };
  }, [containerRef, contentRef, stickyRef, enabled, anchor, anchorOffset]);
}
